import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiFetch } from '../../lib/api';
import { Spinner } from '../../components/Spinner';

/**
 * Conversaciones DIA (2026-07-22) — LOG de mejora continua. Lee dia_log (espejo
 * append-only, migración 0044): TODO lo conversado con Andrés y los agentes en los
 * últimos 5 días, por proyecto y usuario, aunque el usuario haya borrado, editado o
 * relanzado mensajes. Visor read-only para detectar fallas y puntos de mejora.
 */

interface Conversacion {
  chat_id: string;
  proyecto: string;
  chat_titulo: string | null;
  usuarios: string[];
  mensajes: number;
  ediciones: number;
  tokens_in: number;
  tokens_out: number;
  est_cost: number;
  primera: string;
  ultima: string;
}

interface Registro {
  id: string;
  usuario: string | null;
  rol: 'user' | 'andres';
  evento: 'mensaje' | 'edicion';
  contenido: string;
  eventos: { t: string; d: string }[] | null;
  citas: { nombre?: string; ruta?: string }[] | null;
  acciones: { etiqueta?: string; tipo?: string }[] | null;
  adjuntos: { nombre?: string }[] | null;
  tokens_in: number | null;
  tokens_out: number | null;
  est_cost: number | null;
  created_at: string;
}

const fmtHora = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
const fmtDia = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', { weekday: 'short', day: '2-digit', month: 'short' });
const fmtTokens = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

function etiquetaDia(iso: string): string {
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(hoy.getTime() - 86_400_000);
  if (d.toDateString() === hoy.toDateString()) return 'Hoy';
  if (d.toDateString() === ayer.toDateString()) return 'Ayer';
  return fmtDia(iso);
}

export default function ConversacionesDia() {
  const [conversaciones, setConversaciones] = useState<Conversacion[] | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [diaSel, setDiaSel] = useState<string | null>(null);
  const [sel, setSel] = useState<Conversacion | null>(null);
  const [registros, setRegistros] = useState<Registro[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmandoBorrar, setConfirmandoBorrar] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  async function borrarSesion() {
    if (!sel) return;
    setBorrando(true);
    try {
      await apiFetch(`/api/admin/dia-logs/${sel.chat_id}`, { method: 'DELETE' });
      setConversaciones((prev) => (prev ?? []).filter((c) => c.chat_id !== sel.chat_id));
      setSel(null);
      setRegistros(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo borrar la sesión del log.');
    } finally {
      setBorrando(false);
      setConfirmandoBorrar(false);
    }
  }

  async function cargarLista(q: string) {
    try {
      const d = await apiFetch<{ conversaciones: Conversacion[] }>(
        `/api/admin/dia-logs${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      );
      setConversaciones(d.conversaciones);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el log.');
    }
  }

  useEffect(() => {
    void cargarLista('');
  }, []);

  // Búsqueda server-side (contenido de los mensajes) con debounce.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void cargarLista(busqueda.trim()), 400);
    return () => clearTimeout(debounceRef.current);
  }, [busqueda]);

  useEffect(() => {
    if (!sel) return;
    setConfirmandoBorrar(false);
    setRegistros(null);
    apiFetch<{ registros: Registro[] }>(`/api/admin/dia-logs/${sel.chat_id}`)
      .then((d) => setRegistros(d.registros))
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar la conversación.'));
  }, [sel?.chat_id]);

  const dias = useMemo(() => {
    const set = new Map<string, string>();
    for (const c of conversaciones ?? []) {
      const key = new Date(c.ultima).toDateString();
      if (!set.has(key)) set.set(key, etiquetaDia(c.ultima));
    }
    return [...set.entries()];
  }, [conversaciones]);

  const visibles = useMemo(
    () =>
      (conversaciones ?? []).filter(
        (c) => !diaSel || new Date(c.ultima).toDateString() === diaSel,
      ),
    [conversaciones, diaSel],
  );

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="titulo-pagina">Conversaciones DIA</h1>
          <p className="subtitulo-pagina">
            Log completo de los chats con los agentes — últimos 5 días, por proyecto y usuario.
            Sobrevive a mensajes borrados, editados o relanzados.
          </p>
        </div>
      </header>

      {error && <p role="alert" className="alerta-error">{error}</p>}

      {/* Toolbar: búsqueda en contenido + filtro por día */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gris"
            fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4-4" />
          </svg>
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar en el contenido…"
            aria-label="Buscar en el contenido de las conversaciones"
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-[13px] text-texto placeholder:text-gris/70 focus:border-azul-profundo focus:outline-none focus:ring-2 focus:ring-azul-profundo/20"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setDiaSel(null)}
            className={`rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors ${
              diaSel === null
                ? 'bg-azul-profundo text-white ring-azul-profundo'
                : 'bg-white text-texto ring-slate-300 hover:bg-slate-50'
            }`}
          >
            Todos
          </button>
          {dias.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setDiaSel(diaSel === key ? null : key)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors ${
                diaSel === key
                  ? 'bg-azul-profundo text-white ring-azul-profundo'
                  : 'bg-white text-texto ring-slate-300 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[340px,1fr]">
        {/* Lista de conversaciones */}
        <section aria-label="Conversaciones registradas" className={sel ? 'hidden lg:block' : ''}>
          {conversaciones === null ? (
            <div className="flex justify-center py-10"><Spinner tamano="md" /></div>
          ) : visibles.length === 0 ? (
            <p className="tarjeta py-10 text-center text-sm font-light text-gris">
              {busqueda ? 'Sin conversaciones que mencionen ese texto.' : 'Aún no hay conversaciones en la ventana de 5 días.'}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {visibles.map((c) => (
                <li key={c.chat_id}>
                  <button
                    type="button"
                    onClick={() => setSel(c)}
                    className={`w-full rounded-xl border p-3 text-left transition-colors ${
                      sel?.chat_id === c.chat_id
                        ? 'border-azul-profundo/40 bg-azul-profundo/[0.04] ring-1 ring-inset ring-azul-profundo/30'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/60'
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="truncate text-[13px] font-medium text-azul-noche">{c.proyecto}</p>
                      <span className="shrink-0 text-[10.5px] font-light tabular-nums text-gris">
                        {etiquetaDia(c.ultima)} · {fmtHora(c.ultima)}
                      </span>
                    </div>
                    {c.chat_titulo && (
                      <p className="mt-0.5 truncate text-[12px] font-normal text-texto">{c.chat_titulo}</p>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] font-light text-gris">
                      <span className="truncate">{c.usuarios.join(', ') || '—'}</span>
                      <span className="shrink-0 tabular-nums">{c.mensajes} msjs</span>
                      {c.ediciones > 0 && (
                        <span className="shrink-0 rounded bg-amber-50 px-1 text-amber-700 ring-1 ring-inset ring-amber-200">
                          ✎ {c.ediciones}
                        </span>
                      )}
                      <span className="shrink-0 tabular-nums">
                        {fmtTokens(c.tokens_in)}→{fmtTokens(c.tokens_out)} tok
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Transcript */}
        <section aria-label="Transcript de la conversación" className={sel ? '' : 'hidden lg:block'}>
          {!sel ? (
            <div className="tarjeta flex h-full min-h-[280px] items-center justify-center p-8 text-center">
              <p className="text-sm font-light text-gris">
                Selecciona una conversación para leer el transcript completo.
              </p>
            </div>
          ) : (
            <div className="tarjeta flex flex-col p-0">
              {/* Cabecera del transcript */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-100 px-4 py-3">
                <button
                  type="button"
                  onClick={() => { setSel(null); setRegistros(null); }}
                  className="rounded-lg px-2 py-1 text-[12px] font-medium text-azul-profundo hover:bg-azul-profundo/5 lg:hidden"
                >
                  ← Volver
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-azul-noche">
                    {sel.proyecto}{sel.chat_titulo ? ` — ${sel.chat_titulo}` : ''}
                  </p>
                  <p className="truncate text-[11px] font-light text-gris">
                    {sel.usuarios.join(', ')} · {sel.mensajes} mensajes ·{' '}
                    {fmtTokens(sel.tokens_in)} in / {fmtTokens(sel.tokens_out)} out · US${sel.est_cost.toFixed(3)}
                  </p>
                </div>
                {confirmandoBorrar ? (
                  <span className="flex shrink-0 items-center gap-2 text-[12px]">
                    <span className="font-light text-gris">¿Borrar esta sesión del log?</span>
                    <button
                      type="button"
                      onClick={() => void borrarSesion()}
                      disabled={borrando}
                      className="rounded-lg bg-red-600 px-2.5 py-1 font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-60"
                    >
                      {borrando ? 'Borrando…' : 'Borrar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmandoBorrar(false)}
                      disabled={borrando}
                      className="rounded-lg px-2 py-1 font-normal text-gris hover:bg-slate-100"
                    >
                      Cancelar
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmandoBorrar(true)}
                    title="Borrar esta sesión del log (no toca el chat del usuario)"
                    aria-label="Borrar esta sesión del log"
                    className="shrink-0 rounded-lg p-1.5 text-gris transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
                    </svg>
                  </button>
                )}
              </div>

              {registros === null ? (
                <div className="flex justify-center py-12"><Spinner tamano="md" /></div>
              ) : (
                <ol className="flex max-h-[calc(100dvh-19rem)] flex-col gap-4 overflow-y-auto px-4 py-4">
                  {registros.map((r) => {
                    const pensamiento = r.eventos?.find((e) => e.t === 'pensamiento')?.d;
                    const herramientas = r.eventos?.filter((e) => e.t === 'herramienta') ?? [];
                    return (
                      <li key={r.id} className="flex flex-col gap-1">
                        <div className="flex items-baseline gap-2 text-[10.5px] font-light text-gris">
                          <span className={`font-medium ${r.rol === 'user' ? 'text-azul-profundo' : 'text-emerald-700'}`}>
                            {r.rol === 'user' ? (r.usuario ?? 'Usuario') : 'Andrés'}
                          </span>
                          <span className="tabular-nums">{fmtDia(r.created_at)} {fmtHora(r.created_at)}</span>
                          {r.evento === 'edicion' && (
                            <span className="rounded bg-amber-50 px-1.5 text-amber-700 ring-1 ring-inset ring-amber-200">
                              ✎ editó y relanzó desde aquí
                            </span>
                          )}
                          {(r.tokens_out ?? 0) > 0 && (
                            <span className="tabular-nums">{fmtTokens(r.tokens_in ?? 0)}→{fmtTokens(r.tokens_out ?? 0)} tok</span>
                          )}
                        </div>

                        <div
                          className={`rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                            r.rol === 'user'
                              ? 'bg-azul-profundo/[0.05] text-texto ring-1 ring-inset ring-azul-profundo/10'
                              : 'border border-slate-200 bg-white text-texto'
                          }`}
                        >
                          {pensamiento && (
                            <details className="mb-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-[12px]">
                              <summary className="cursor-pointer select-none font-medium text-gris">
                                🧠 Pensamiento ({pensamiento.length.toLocaleString('es-CL')} caracteres)
                              </summary>
                              <p className="mt-1.5 whitespace-pre-wrap font-light text-gris">{pensamiento}</p>
                            </details>
                          )}
                          <div className="prosa-chat break-words [&>*+*]:mt-2 [&_table]:block [&_table]:overflow-x-auto">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{r.contenido}</ReactMarkdown>
                          </div>
                          {(r.adjuntos?.length ?? 0) > 0 && (
                            <p className="mt-2 text-[11px] font-light text-gris">
                              📎 {r.adjuntos!.map((a) => a.nombre ?? 'adjunto').join(' · ')}
                            </p>
                          )}
                          {(herramientas.length > 0 || (r.citas?.length ?? 0) > 0 || (r.acciones?.length ?? 0) > 0) && (
                            <div className="mt-2 flex flex-wrap gap-1.5 border-t border-slate-100 pt-2">
                              {herramientas.map((h, i) => (
                                <span key={i} className="rounded bg-slate-100 px-1.5 py-px text-[10.5px] font-normal text-slate-700" title={h.d}>
                                  🔧 {h.d.length > 48 ? `${h.d.slice(0, 48)}…` : h.d}
                                </span>
                              ))}
                              {(r.citas?.length ?? 0) > 0 && (
                                <span className="rounded bg-celeste/20 px-1.5 py-px text-[10.5px] font-normal text-azul-profundo" title={r.citas!.map((c) => c.nombre ?? c.ruta ?? '').join('\n')}>
                                  📄 {r.citas!.length} citas
                                </span>
                              )}
                              {r.acciones?.map((a, i) => (
                                <span key={`a${i}`} className="rounded bg-azul-profundo/10 px-1.5 py-px text-[10.5px] font-normal text-azul-profundo">
                                  ⚡ {a.etiqueta ?? a.tipo}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
